import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { fetchTransportNotifications, fetchGenericNotifications } from "../../framework/redux/slices/notificationSlice";
import { useAppOptionValueEquals } from "../framework/useAppOptionValue";
import {
    optionOwnerHasTransportNotificationsEnabled,
    OptionPublishedWorkShiftNotificationsEnabled,
} from "../../models/common/consts";

// Thirty seconds
const pollingInterval = 1000 * 30;

export const useNotificationPolling = (employeeId: string, interval: number = pollingInterval) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const unconfirmedCount = useAppSelector((state) => state.notification.unconfirmedCount);
    const previousNotificationCount = useRef<number | null>(null);

    const [orderNotificationsEnabled] = useAppOptionValueEquals(
        optionOwnerHasTransportNotificationsEnabled,
        true
    );
    const [plannedShiftNotificationsEnabled] = useAppOptionValueEquals(
        OptionPublishedWorkShiftNotificationsEnabled,
        true
    );

    // Extract only the pathname to avoid unnecessary re-renders
    const { pathname } = location;

    useEffect(() => {
        if (!employeeId) return;

        const poll = () => {
            if (plannedShiftNotificationsEnabled) {
                void dispatch(fetchGenericNotifications());
            }

            if (orderNotificationsEnabled) {
                void dispatch(fetchTransportNotifications(employeeId));
            }
        };

        const intervalId = setInterval(poll, interval);
        poll();
        return () => clearInterval(intervalId);
    }, [
        employeeId,
        interval,
        dispatch,
        orderNotificationsEnabled,
        plannedShiftNotificationsEnabled,
    ]);

    useEffect(() => {
        if (pathname.startsWith("/login")) {
            return;
        }

        if (previousNotificationCount.current === null) {
            previousNotificationCount.current = unconfirmedCount;
            return;
        }

        const hasNotifications = unconfirmedCount > 0;
        const previousCount = previousNotificationCount.current;

        if (hasNotifications) {
            const onTransportOrderDetails = pathname.startsWith("/transport/order");
            if (!pathname.startsWith("/notifications") && !onTransportOrderDetails) {
                navigate("/notifications", { replace: true });
            }
        } else if (previousCount > 0 && unconfirmedCount === 0) {
            if (pathname.startsWith("/notifications")) {
                navigate("/", { replace: true });
            }
        }

        previousNotificationCount.current = unconfirmedCount;
    }, [unconfirmedCount, navigate, pathname]);
};
